import { useEffect, useMemo, useState } from "react";

const PERMISSION_ERROR_MESSAGE = "Please grant camera & mic access to live stream.";
export default function useMediaDevices(
    onAudioInputSelect?: (device: MediaDeviceInfo) => void,
    onVideoInputSelect?: (device: MediaDeviceInfo) => void,
) {
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedAudioInput, setSelectedAudioInput] = useState<MediaDeviceInfo | null>(null);
    const [selectedVideoInput, setSelectedVideoInput] = useState<MediaDeviceInfo | null>(null);

    const audioDevices = useMemo(() => devices.filter((device) => device.kind === "audioinput"), [devices]);
    const videoDevices = useMemo(() => devices.filter((device) => device.kind === "videoinput"), [devices]);

    const updateDevicesList = () => {
        navigator.mediaDevices
            .getUserMedia({ audio: true, video: true })
            .then(() => navigator.mediaDevices.enumerateDevices())
            .then((devices) => setDevices(devices.filter((d: MediaDeviceInfo) => !d.label.includes("Default"))))
            .catch(() => alert(PERMISSION_ERROR_MESSAGE));
    };

    useEffect(() => {
        updateDevicesList();

        navigator.mediaDevices.addEventListener("devicechange", updateDevicesList);

        return () => {
            navigator.mediaDevices.removeEventListener("devicechange", updateDevicesList);
        };
    }, []);

    // Initialize the first selected audio input if none is set
    useEffect(() => {
        if (selectedAudioInput == null && audioDevices && audioDevices[0]) {
            setSelectedAudioInput(audioDevices[0]);
            onAudioInputSelect?.(audioDevices[0]);
        }
    }, [selectedAudioInput, setSelectedAudioInput, audioDevices]);

    // Initialize the first selected video input if none is set
    useEffect(() => {
        if (selectedVideoInput == null && videoDevices && videoDevices[0]) {
            setSelectedVideoInput(videoDevices[0]);
            onVideoInputSelect?.(videoDevices[0]);
        }
    }, [selectedVideoInput, setSelectedVideoInput, videoDevices]);

    const selectAudioInput = (device: MediaDeviceInfo) => {
        setSelectedAudioInput(device);
    };

    const selectVideoInput = (device: MediaDeviceInfo) => {
        setSelectedVideoInput(device);
    };

    return {
        audioDevices,
        selectedAudioInput,
        selectAudioInput,
        videoDevices,
        selectedVideoInput,
        selectVideoInput,
    } as const;
}
